/**
 * Autoridad unica de rol.
 *
 * Antes de este modulo habia CUATRO criterios distintos, y no siempre coincidian:
 *   1. TenantContext        — user_profiles.role, con fallback que hardcodeaba 'employee'
 *   2. DashboardPanel       — re-consultaba employees y podia sobreescribir el rol visible
 *   3. RPC set_employee_role— trata employees.role como origen y lo espeja a user_profiles
 *   4. Postgres current_user_role() — MAXIMO PRIVILEGIO entre las dos tablas
 *
 * El criterio canonico es el 4, porque es el unico que ya decide autorizacion REAL
 * de datos: la RLS lo consulta en cada query. Si el frontend resolviera distinto,
 * la UI y la base estarian en desacuerdo por construccion. Asi que este modulo
 * ESPEJA `public.current_user_role()` — cualquier cambio aqui debe cambiarse alla,
 * y viceversa.
 *
 * Definicion SQL vigente (verificada en produccion):
 *
 *   SELECT COALESCE(
 *     (SELECT r FROM (
 *        SELECT role AS r FROM employees     WHERE user_id = auth.uid()
 *        UNION ALL
 *        SELECT role AS r FROM user_profiles WHERE id      = auth.uid()
 *      ) roles
 *      WHERE r IS NOT NULL
 *      ORDER BY CASE r WHEN 'superadmin' THEN 5 ... END DESC
 *      LIMIT 1),
 *     'employee')
 *
 * Nota sobre 'admin': NO es un rol de producto (los roles son owner / superadmin /
 * manager / employee), pero es un valor REAL y muy extendido en la base:
 * api/register-business.js escribe user_profiles.role='owner' y employees.role='admin'
 * para el mismo usuario en toda cuenta nueva. Borrar 'admin' de estas listas seria
 * un cambio de permisos, no una limpieza.
 */

// Escalafon de privilegio. Espeja el CASE de current_user_role() en Postgres.
export const ROLE_RANK = Object.freeze({
    superadmin: 5,
    owner:      4,
    admin:      3,
    manager:    2,
    employee:   1,
});

// Igual que el COALESCE final del SQL: sin candidato valido, el rol menos
// privilegiado. Fail-closed.
export const DEFAULT_ROLE = 'employee';

/**
 * Resuelve el rol efectivo a partir de los candidatos disponibles.
 * Gana el de mayor privilegio, igual que current_user_role().
 *
 * Un rol desconocido (que no este en ROLE_RANK) puntua 0 pero sigue siendo
 * candidato — mismo comportamiento que el `ELSE 0` del CASE en SQL: si es el
 * unico valor presente, se devuelve tal cual en vez de inventar uno.
 *
 * @param {...(string|null|undefined)} candidates p.ej. (user_profiles.role, employees.role)
 * @returns {string} el rol efectivo; DEFAULT_ROLE si no hubo ningun candidato
 */
export const resolveRole = (...candidates) => {
    let best = null;
    let bestRank = -1;
    for (const candidate of candidates) {
        if (candidate === null || candidate === undefined || candidate === '') continue;
        const rank = ROLE_RANK[candidate] ?? 0;
        if (rank > bestRank) {
            bestRank = rank;
            best = candidate;
        }
    }
    return best ?? DEFAULT_ROLE;
};

/**
 * Los cuatro valores que la app trata como "personal administrativo".
 * Es el predicado que estaba copiado literalmente 6 veces en Layout.jsx
 * (L97, L124, L139, L654) y repetido en DashboardPanel y EditTransactionModal.
 */
export const ROLES_ADMIN = Object.freeze(['admin', 'owner', 'superadmin', 'manager']);

export const isAdminRole = (role) => ROLES_ADMIN.includes(role);

/**
 * Subconjunto DUEÑO: excluye a manager a proposito.
 * Lo usa el agente de WhatsApp ("never regular employees", Layout.jsx:129) y es
 * la unica seccion administrativa que un gerente NO ve. Si esto se fusionara con
 * ROLES_ADMIN, un gerente ganaria acceso al agente — que es exactamente el tipo
 * de cambio de permisos que este refactor no debe introducir.
 */
export const ROLES_OWNER = Object.freeze(['admin', 'owner', 'superadmin']);

export const isSuperAdminRole = (role) => role === 'superadmin';
